import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { extractFieldErrors, type PracticeDetail } from '../../../../services/practiceDetail';
import { useUpdatePracticeDetail } from '../../../../hooks/usePracticeDetail';
import {
    buildPracticeDetailUpdate,
    flattenPracticeDetail,
    maxLengthFieldError,
    PRACTICE_FIELD_DEFS,
    requiredFieldError,
    resolveFieldOptions,
    selectFieldError
} from './fieldDefinitions';
import type { DetailFieldState } from './DetailField';

const SAVED_BANNER_MS = 2500;

export type FormPhase = 'idle' | 'dirty' | 'saving' | 'error' | 'saved';

// Both checks below run entirely client-side: required-field emptiness
// (name/drFirstName/drLastName - the server rejects a null there) and
// enum/dropdown membership (the backend no longer checks a select value
// against Monday's live labels). Only the fields the user actually edited
// are checked - an out-of-range legacy value already on the record is left
// alone unless the user touches that specific field, so it never blocks
// saving everything else. Uses the raw resolveFieldOptions() list (not the
// current-value-augmented one the dropdown renders from) - see
// selectFieldError's own comment for why that's exactly what keeps this rule
// correct without extra bookkeeping here.
function validateDirtyFields(
    dirtyKeys: string[],
    edits: Record<string, string>
): Record<string, string> {
    const errors: Record<string, string> = {};
    for (const key of dirtyKeys) {
        const field = PRACTICE_FIELD_DEFS[key];
        if (!field) continue;
        const value = edits[key];
        const options = resolveFieldOptions(field);
        const message =
            requiredFieldError(field, value) ??
            selectFieldError(field, value, options) ??
            maxLengthFieldError(field, value);
        if (message) errors[key] = message;
    }
    return errors;
}

// Message for a save held back by *local* validation, composed from each
// field's own error text (already phrased to read naturally after the
// field's label - "can't be empty", "isn't one of the allowed options").
function localValidationBanner(errors: Record<string, string>): string {
    const keys = Object.keys(errors);
    if (keys.length === 1) {
        const label = PRACTICE_FIELD_DEFS[keys[0]]?.label ?? 'That field';
        return `${label} ${errors[keys[0]]}, so the whole save was held back.`;
    }
    return 'One or more fields need fixing before this can be saved.';
}

/**
 * Owns dirty tracking and the atomic save for the practice Details form.
 *
 * The save is all-or-nothing: on a local validation failure or a 400, every
 * pending edit is kept exactly as typed (`edits` is never cleared) and only
 * the offending field(s) are marked `failed` - the rest stay `changed`,
 * ready to resubmit once the named field is fixed.
 */
export function usePracticeDetailsForm(practiceId: string, practice: PracticeDetail | undefined) {
    const { showToast } = useToast();
    const { mutateAsync, isPending } = useUpdatePracticeDetail(practiceId);

    const originalValues = useMemo(
        () => (practice ? flattenPracticeDetail(practice) : {}),
        [practice]
    );
    const [edits, setEdits] = useState<Record<string, string>>({});
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [phase, setPhase] = useState<FormPhase>('idle');
    const [bannerMessage, setBannerMessage] = useState<string | null>(null);
    const [savedCount, setSavedCount] = useState(0);
    const savedTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    // Reset to a clean slate whenever a *different* record (or a freshly
    // re-fetched updatedAt) lands - not on every render.
    const practiceStamp = practice ? `${practice.id}:${practice.updatedAt}` : null;
    const lastStampRef = useRef<string | null>(null);
    useEffect(() => {
        if (practiceStamp && practiceStamp !== lastStampRef.current) {
            lastStampRef.current = practiceStamp;
            setEdits({});
            setFieldErrors({});
            setPhase('idle');
            setBannerMessage(null);
        }
    }, [practiceStamp]);

    useEffect(() => () => clearTimeout(savedTimeoutRef.current), []);

    const values = useMemo(() => ({ ...originalValues, ...edits }), [originalValues, edits]);

    const dirtyKeys = useMemo(
        () => Object.keys(edits).filter((key) => edits[key] !== (originalValues[key] ?? '')),
        [edits, originalValues]
    );
    const dirtyCount = phase === 'saved' ? savedCount : dirtyKeys.length;
    const isDirty = dirtyKeys.length > 0;
    const dirtyLabels = useMemo(
        () => dirtyKeys.map((key) => PRACTICE_FIELD_DEFS[key]?.label ?? key),
        [dirtyKeys]
    );
    const failedFieldKeys = useMemo(() => Object.keys(fieldErrors), [fieldErrors]);
    // discardFieldEdits can empty the form; with no edits left there is nothing to save or fix.
    const hasEdits = Object.keys(edits).length > 0;
    const shownPhase = !hasEdits && (phase === 'dirty' || phase === 'error') ? 'idle' : phase;

    const onFieldChange = useCallback(
        (key: string, value: string) => {
            clearTimeout(savedTimeoutRef.current);
            setEdits((prev) => ({ ...prev, [key]: value }));
            const hadError = key in fieldErrors;
            if (hadError) {
                setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                });
            }
            setBannerMessage(null);
            setPhase((prev) => {
                if (prev !== 'error') return 'dirty';
                const stillFailing = Object.keys(fieldErrors).some((k) => k !== key);
                return stillFailing ? 'error' : 'dirty';
            });
        },
        [fieldErrors]
    );

    const scheduleSavedDismiss = useCallback(() => {
        clearTimeout(savedTimeoutRef.current);
        savedTimeoutRef.current = setTimeout(() => setPhase('idle'), SAVED_BANNER_MS);
    }, []);

    const onSave = useCallback(async () => {
        if (dirtyKeys.length === 0) return;
        const changesCount = dirtyKeys.length;

        // Caught before the round trip: instant feedback, and the backend no
        // longer checks this itself.
        const localErrors = validateDirtyFields(dirtyKeys, edits);
        if (Object.keys(localErrors).length > 0) {
            setFieldErrors(localErrors);
            setBannerMessage(localValidationBanner(localErrors));
            setPhase('error');
            return;
        }

        setPhase('saving');
        setBannerMessage(null);

        const changes = buildPracticeDetailUpdate(dirtyKeys, edits);

        try {
            await mutateAsync(changes);
            setEdits({});
            setFieldErrors({});
            setSavedCount(changesCount);
            setPhase('saved');
            scheduleSavedDismiss();
        } catch (err) {
            const errors = extractFieldErrors(err);
            setFieldErrors(errors ?? {});
            setBannerMessage(
                apiErrorMessage(
                    err,
                    'Nothing was saved - something went wrong, and your changes are still pending below.'
                )
            );
            setPhase('error');
            showToast('Save failed - your changes were kept, nothing else was touched.', 'error');
        }
    }, [dirtyKeys, edits, mutateAsync, scheduleSavedDismiss, showToast]);

    const onDiscard = useCallback(() => {
        clearTimeout(savedTimeoutRef.current);
        setEdits({});
        setFieldErrors({});
        setBannerMessage(null);
        setPhase('idle');
    }, []);

    // A save made outside the form (e.g. the DM link) must not be shadowed by a stale typed value.
    const discardFieldEdits = useCallback(
        (keys: string[]) => {
            const keep = <T>(record: Record<string, T>) =>
                Object.fromEntries(Object.entries(record).filter(([key]) => !keys.includes(key)));
            setEdits(keep);
            setFieldErrors(keep);
            // Only when it drops the last failed field: a save error with no field errors stays an error.
            const failed = Object.keys(fieldErrors);
            if (failed.length > 0 && failed.every((key) => keys.includes(key))) {
                setPhase((prev) => (prev === 'error' ? 'dirty' : prev));
            }
        },
        [fieldErrors]
    );

    const scrollToField = useCallback((key: string) => {
        const node = document.getElementById(`practice-field-${key}`);
        if (!node) return;
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (node instanceof HTMLElement) node.focus({ preventScroll: true });
    }, []);

    const fieldState = useCallback(
        (key: string): DetailFieldState => {
            if (phase === 'saving') return dirtyKeys.includes(key) ? 'saving' : 'idle';
            if (phase === 'saved') return 'idle';
            if (key in fieldErrors) return 'failed';
            if (dirtyKeys.includes(key)) return 'changed';
            return 'idle';
        },
        [phase, dirtyKeys, fieldErrors]
    );

    const fieldError = useCallback((key: string) => fieldErrors[key], [fieldErrors]);

    return {
        values,
        fieldState,
        fieldError,
        isDirty,
        dirtyCount,
        dirtyLabels,
        phase: shownPhase,
        bannerMessage,
        failedFieldKeys,
        isSaving: isPending,
        onFieldChange,
        onSave: () => void onSave(),
        onDiscard,
        discardFieldEdits,
        scrollToField
    };
}
